// MARK://dhk8/pos/lib/bridge/relay-client
// Cliente REST del Print Relay (impresión por la nube). Habla al servicio
// `pos-printing` vía el gateway (api.<tld> → /v1/dhk8-pos-printing/* → strip
// del prefijo → /v1/print/*). Contrato = docs/print-relay-design.md.
//
// Por qué existe (vs bridge-client loopback): el POS es https y un Bridge LAN es
// http a una IP local → el navegador bloquea el mixed-content (solo 127.0.0.1
// está exento). En vez de hablar a la LAN, el POS ENCOLA el job en la nube; el
// Bridge HOST de la estación mantiene una conexión saliente, jala sus jobs,
// imprime local y hace ack. Cero navegador→LAN.
//
// Auth: JWT (Bearer) + headers de tenant los pone `apiFetch`/`apiData` (lib/api).
// El backend deriva (tenantId, env) del JWT — aquí NO se mandan. El `pollSecret`
// del bridge NUNCA pasa por aquí (solo bridge↔nube).
//
// Rutas (post-gateway el servicio ve /v1/print/...):
//   POST …/print/stations/resolve { pairingCode }      → { stationId, name }
//   POST …/print/jobs { stationId, kind, payload,       → { jobId, status }
//                       copies, idempotencyKey }
//   GET  …/print/jobs/:id                                → RelayJob (status/attempts/…)
//   GET  …/print/jobs?stationId&limit                    → { items: RelayJob[] } | RelayJob[]
//   POST …/print/jobs/:id/retry                          → RelayJob
//   GET  …/print/stations/:id  (status/heartbeat)        → RelayStationStatus
//
// Envelope del servicio: `{ ok, data }` — lo desenvuelve apiData().

import { apiData, svcUrl } from '../api.js'

// El gateway enruta /v1/dhk8-pos-printing/* al servicio pos-printing (que strip-ea
// el prefijo y ve /v1/print/relay/*). El relay vive bajo /print/relay/* para no
// colisionar con el CRUD legacy Pi-edge en /print/jobs. Mismo patrón svcUrl que pos-*.
const SERVICE = 'dhk8-pos-printing'
function relayUrl(path: string): string {
  return svcUrl(SERVICE, `/v1/print/relay${path.startsWith('/') ? path : `/${path}`}`)
}

// ── tipos del contrato ─────────────────────────────────────────────────────────

/** Estado de un PrintJob en el modelo del relay (db-pos). FIFO por estación:
 *  pending → claimed → done | failed (con reintentos). Ver máquina de estados
 *  en docs/print-relay-design.md. */
export type RelayJobStatus = 'pending' | 'claimed' | 'done' | 'failed'

/** Tipo de impresión que entiende el relay (≠ PrintKind del bridge loopback). */
export type RelayJobKind = 'receipt' | 'kitchen' | 'raw' | 'drawer'

/** Job tal como lo devuelve el relay. Campos tolerantes: builds intermedios del
 *  backend pueden omitir alguno; el panel de troubleshooting degrada con gracia. */
export interface RelayJob {
  id: string
  stationId?: string
  kind?: RelayJobKind | string
  status: RelayJobStatus | string
  attempts: number
  maxAttempts: number
  lastError?: string | null
  copies?: number
  idempotencyKey?: string | null
  createdAt?: string
  claimedAt?: string | null
  doneAt?: string | null
}

/** Resultado de resolver un pairingCode → la estación de ESTE tenant+env. */
export interface ResolvedStation {
  stationId: string
  name: string
}

/** Estado de una estación (online = lastSeenAt < 20s, lo decide el backend). */
export interface RelayStationStatus {
  id: string
  name: string
  online: boolean
  lastSeenAt?: string | null
}

/** Respuesta de encolar un job. */
export interface EnqueuedJob {
  jobId: string
  status: RelayJobStatus | string
}

// ── helpers ──────────────────────────────────────────────────────────────────

/** Normaliza un job del backend: el DTO usa `jobId` (toJobDto), pero el resto del POS
 *  (panel, tracker, Reimprimir) usa `id`. Sin esto `id` queda undefined y Reimprimir
 *  pega a `/jobs/undefined/retry` → "no se encuentra". Mismo desajuste jobId↔id que el bridge. */
function normJob(raw: (RelayJob & { jobId?: string }) | null | undefined): RelayJob {
  const r = (raw ?? {}) as RelayJob & { jobId?: string }
  return { ...r, id: r.id ?? r.jobId ?? '' }
}

/** Lista de jobs: el backend puede responder `{ items: [...] }` o el array pelón. */
function asJobArray(body: { items?: RelayJob[] } | RelayJob[] | null | undefined): RelayJob[] {
  const arr = Array.isArray(body) ? body : (body && Array.isArray(body.items) ? body.items : [])
  return arr.map(normJob)
}

// ── operaciones ────────────────────────────────────────────────────────────────

export interface EnqueueJobInput {
  stationId: string
  kind: RelayJobKind
  /** CanonicalReceipt (receipt/kitchen) o, para 'raw', el payload pre-rendereado.
   *  Se serializa a JSON por apiFetch; el backend lo guarda como payloadJson. */
  payload: unknown
  /** Número de copias (1..N). El bridge imprime `copies` veces el mismo job. */
  copies?: number
  /** Clave de idempotencia (única por tenant+station) — evita doble impresión en
   *  reintentos de red. El caller la genera estable por intento lógico. */
  idempotencyKey: string
}

/** POST …/print/jobs — encola un trabajo para la estación. Devuelve { jobId, status }. */
export async function enqueueJob(input: EnqueueJobInput): Promise<EnqueuedJob> {
  return apiData<EnqueuedJob>(relayUrl('/jobs'), {
    method: 'POST',
    body: {
      stationId: input.stationId,
      kind: input.kind,
      payload: input.payload,
      copies: input.copies ?? 1,
      idempotencyKey: input.idempotencyKey,
    },
  })
}

/** POST …/print/stations/resolve — el dispositivo adicional teclea el pairingCode
 *  corto; el backend valida mismo (tenant, env) del JWT y devuelve { stationId, name }.
 *  404 si el código no resuelve dentro de este tenant+env. */
export async function resolveStation(pairingCode: string): Promise<ResolvedStation> {
  return apiData<ResolvedStation>(relayUrl('/stations/resolve'), {
    method: 'POST',
    body: { pairingCode: pairingCode.trim() },
  })
}

/** GET …/print/jobs/:id — seguir el progreso de un job (status/attempts/lastError). */
export async function getJob(id: string, signal?: AbortSignal): Promise<RelayJob> {
  return normJob(await apiData<RelayJob>(relayUrl(`/jobs/${encodeURIComponent(id)}`), signal ? { signal } : undefined))
}

/** GET …/print/jobs?stationId&limit — jobs recientes de la estación (panel de
 *  troubleshooting). FIFO/recientes primero según el backend. */
export async function listJobs(stationId: string, limit = 10, signal?: AbortSignal): Promise<RelayJob[]> {
  const qs = new URLSearchParams({ stationId, limit: String(limit) })
  const body = await apiData<{ items?: RelayJob[] } | RelayJob[]>(
    relayUrl(`/jobs?${qs.toString()}`),
    signal ? { signal } : undefined,
  )
  return asJobArray(body)
}

/** POST …/print/jobs/:id/retry — re-encola manualmente (status→pending, nextAttemptAt=now). */
export async function retryJob(id: string): Promise<RelayJob> {
  return normJob(await apiData<RelayJob>(relayUrl(`/jobs/${encodeURIComponent(id)}/retry`), { method: 'POST' }))
}

/** GET …/print/stations/:id — estado de la estación (online/offline + último heartbeat). */
export async function getStationStatus(stationId: string, signal?: AbortSignal): Promise<RelayStationStatus> {
  return apiData<RelayStationStatus>(
    relayUrl(`/stations/${encodeURIComponent(stationId)}`),
    signal ? { signal } : undefined,
  )
}

// ── HOST → nube: leer identidad del bridge local + registrar estación ──────────
// Estas dos funciones son el "lado HOST" del pairing (docs/print-relay-design.md):
// la web HOST (en el MISMO equipo que el bridge) lee el pairingCode+pollSecret del
// bridge por loopback y registra la estación en la nube. El backend guarda solo el
// hash del pollSecret; a partir de ahí el bridge autentica su long-poll y los
// dispositivos satélite vinculan tecleando el pairingCode.

/** Base loopback del bridge local (mismo equipo). 127.0.0.1 es origen seguro, así
 *  que la página https del POS SÍ puede fetch-earlo (mixed-content no aplica a
 *  loopback). En un satélite sin bridge local esto simplemente falla → correcto. */
const BRIDGE_LOOPBACK = 'http://127.0.0.1:9101'

/** Identidad de emparejamiento que expone el bridge local (loopback-only). */
export interface BridgeRelayIdentity {
  pairingCode: string
  /** Secreto largo del bridge; se usa UNA vez para registrar (el backend guarda
   *  solo su hash). El bridge lo expone SOLO en loopback (403 desde la LAN). */
  pollSecret: string
  suggestedName?: string
  /** false ⇒ el bridge corre pero SIN relay (falta NINTAIGO_BRIDGE_RELAY_URL):
   *  registrar crearía una estación que nunca pollea (siempre offline). */
  relayEnabled?: boolean
}

/** Estación tal como la devuelve el registro (RelayStationDto del backend). */
export interface RegisteredStation {
  stationId: string
  name: string
  env?: string | null
  branchId?: string | null
  online?: boolean
  lastSeenAt?: string | null
}

export interface RegisterStationInput {
  name: string
  pairingCode: string
  pollSecret: string
}

/** GET loopback /v1/relay/identity — SOLO funciona en la máquina HOST (donde corre
 *  el bridge). Devuelve pairingCode + pollSecret para registrar la estación. */
export async function getBridgeIdentity(timeoutMs = 2500): Promise<BridgeRelayIdentity> {
  const signal =
    typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal
      ? (AbortSignal as unknown as { timeout: (n: number) => AbortSignal }).timeout(timeoutMs)
      : undefined
  let res: Response
  try {
    res = await fetch(`${BRIDGE_LOOPBACK}/v1/relay/identity`, signal ? { signal } : {})
  } catch {
    throw new Error('No se pudo leer el Bridge local (127.0.0.1:9101). ¿Está instalado y corriendo en ESTE equipo?')
  }
  if (res.status === 403) throw new Error('La identidad del Bridge solo se lee desde el mismo equipo donde corre el Bridge.')
  if (!res.ok) throw new Error(`El Bridge devolvió HTTP ${res.status} al leer su identidad.`)
  const body = (await res.json().catch(() => null)) as Partial<BridgeRelayIdentity> | null
  if (!body || !body.pairingCode || !body.pollSecret) {
    throw new Error('El Bridge no expone credenciales de relay. Actualízalo a la versión con impresión por la nube.')
  }
  return {
    pairingCode: body.pairingCode,
    pollSecret: body.pollSecret,
    ...(body.suggestedName ? { suggestedName: body.suggestedName } : {}),
    ...(typeof body.relayEnabled === 'boolean' ? { relayEnabled: body.relayEnabled } : {}),
  }
}

/** POST …/print/relay/stations/register — la web HOST registra/actualiza la estación
 *  en la nube; el backend la liga a (tenant, env, sucursal) del JWT y guarda solo el
 *  hash del pollSecret. Idempotente por (tenant, env, name|pairingCode). */
export async function registerStation(input: RegisterStationInput): Promise<RegisteredStation> {
  return apiData<RegisteredStation>(relayUrl('/stations/register'), {
    method: 'POST',
    body: { name: input.name, pairingCode: input.pairingCode, pollSecret: input.pollSecret },
  })
}

export const relayClient = {
  enqueueJob,
  resolveStation,
  getJob,
  listJobs,
  retryJob,
  getStationStatus,
  getBridgeIdentity,
  registerStation,
}
export default relayClient
